import AfricaSvg from "@/components/svg/AfricaSvg";
import AsiaSvg from "@/components/svg/AsiaSvg";
import EuropeSvg from "@/components/svg/EuropeSvg";
import LatinAmericaSvg from "@/components/svg/LatinAmericaSvg";
import NorthAmericaSvg from "@/components/svg/NorthAmericaSvg";
import OceaniaSvg from "@/components/svg/OceaniaSvg";
import SouthAmericaSvg from "@/components/svg/SouthAmericaSvg";
import { Continent } from "@/types/continent";
import { useCallback, useEffect, useRef, useState } from "react";

type FilterButtonProps = {
  value: Continent[];
  onChange: (value: Continent[]) => void;
  counts: Record<Continent, number>;
};

const CONTINENTS_CONFIG: {
  value: Continent;
  label: string;
  color: string;
  Icon: React.FC<{ style?: React.CSSProperties }>;
}[] = [
  { value: "europe", label: "Európa", color: "#0085C7", Icon: EuropeSvg },
  { value: "asia", label: "Ázia", color: "#F4C300", Icon: AsiaSvg },
  { value: "africa", label: "Afrika", color: "#000000", Icon: AfricaSvg },
  {
    value: "south-america",
    label: "Južná Amerika",
    color: "#FFAC1C",
    Icon: SouthAmericaSvg,
  },
  {
    value: "latin-america",
    label: "Stredná Amerika",
    color: "#1F7A6D",
    Icon: LatinAmericaSvg,
  },
  {
    value: "north-america",
    label: "Severná Amerika",
    color: "#DF0024",
    Icon: NorthAmericaSvg,
  },
  { value: "oceania", label: "Oceánia", color: "#009F3D", Icon: OceaniaSvg },
];

function isActive(continent: Continent, value: Continent[]): boolean {
  return value.includes(continent);
}

export default function FilterButtons({
  value,
  onChange,
  counts,
}: FilterButtonProps) {
  const visibleContinents = CONTINENTS_CONFIG.filter(
    (c) => counts[c.value] > 0,
  );

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const handleToggle = useCallback(
    (continent: Continent) => {
      if (value[0] === continent) {
        onChange([]);
      } else {
        onChange([continent]);
      }
    },
    [value, onChange],
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleScroll = () => {
      const maxIndex = visibleContinents.length - 1;

      // Force last dot when at end (iOS-safe)
      if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 2) {
        setActiveIndex(maxIndex);
        return;
      }

      const ratio = el.scrollLeft / (el.scrollWidth - el.clientWidth);
      const index = Math.round(ratio * maxIndex);
      setActiveIndex(index);
    };

    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [visibleContinents.length]);

  return (
    <>
      <div className='relative w-full xl:flex xl:justify-center'>
        <div
          ref={scrollRef}
          className='
            flex md:gap-4 w-full overflow-x-auto overflow-y-hidden md:justify-center no-scrollbar px-10
            
          '
          style={{
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0px, black clamp(25px,5vw,30px), black calc(100% - clamp(30px,7vw,30px)), transparent 100%)",
            maskImage:
              "linear-gradient(to right, transparent 0px, black clamp(25px,5vw,30px), black calc(100% - clamp(30px,7vw,30px)), transparent 100%)",
            WebkitMaskRepeat: "no-repeat",
            maskRepeat: "no-repeat",
            WebkitMaskSize: "100% 100%",
            maskSize: "100% 100%",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          {visibleContinents.map((c) => {
            const active = isActive(c.value, value);
            const anySelected = value.length > 0;

            const Icon = c.Icon;

            const buttonStyle: React.CSSProperties = {
              width: "clamp(150px, 22vw, 170px)",
              height: "clamp(150px, 22vw, 150px)",
              flex: "0 0 auto",
              cursor: "pointer",
              filter: anySelected && !active ? "opacity(0.35)" : "none",
              transition: "filter 150ms ease, outline-offset 150ms ease",
            };

            const iconStyle: React.CSSProperties = {
              width: "100%",
              height: "5.5rem",
              maxHeight: "100%",
              transform: "scale(2.6)",
              transformOrigin: "center",
              display: "block",
              color: c.color,
              pointerEvents: "none",
            };

            return (
              <button
                type='button'
                key={c.value}
                onClick={() => handleToggle(c.value)}
                className='continent-btn mr-1 transition-all'
                style={buttonStyle}
              >
                <div className='w-full h-full grid grid-rows-[6.5rem_2.75rem_auto] place-items-center'>
                  {/* Icon */}
                  <div className='w-full mt-2 mx-1.5 flex items-center justify-center'>
                    <Icon style={iconStyle} />
                  </div>
                  {/* Continent name */}
                  <div
                    className='flex flex-col mt-3 justify-end font-kalam font-semibold leading-tight text-center'
                    style={{ height: "2.5rem", justifyContent: "center" }}
                  >
                    {c.label.split(" ").map((word, i) => (
                      <div key={i}>{word}</div>
                    ))}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        <div className='mt-3 flex justify-center gap-2 md:hidden'>
          {visibleContinents.map((_, i) => (
            <div
              key={i}
              className={`rounded-full transition-all
              ${
                activeIndex === i
                  ? "w-3 h-3 bg-gray-600"
                  : "w-2.5 h-2.5 bg-gray-400 opacity-50"
              }`}
            />
          ))}
        </div>
      </div>
    </>
  );
}
